import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Alert, Typography } from '@mui/material';
import { io } from 'socket.io-client';
import { bindGameResync } from '../../../../utils/bindGameResync';
import { useSnackbar } from 'notistack';
import RapidQuizRules from './RapidQuizRules';
import RapidQuizWaiting from './RapidQuizWaiting';
import QuizQuestion from '../normalMode/QuizQuestion';
import RapidQuizResults from './RapidQuizResults';

const RAPID_QUIZ_STEPS = {
  RULES: 'rules',
  WAITING: 'waiting',
  QUESTION: 'question',
  RESULTS: 'results'
};

const RapidQuizMain = ({ session, participant, onBack, onComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State management
  const [currentStep, setCurrentStep] = useState(RAPID_QUIZ_STEPS.RULES);
  const [currentSong, setCurrentSong] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [finalPosition, setFinalPosition] = useState(0);
  const [finalLeaderboard, setFinalLeaderboard] = useState([]);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [lastPushedAt, setLastPushedAt] = useState(null);
  const [questionVersion, setQuestionVersion] = useState(0); // forces remount on song switch
  const [resumeInfo, setResumeInfo] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);

  // Refs to stabilize polling and prevent refresh loops
  const lastPushedAtRef = useRef(lastPushedAt);
  const currentSongIdRef = useRef(currentSong?.songs_master_id || null);
  const currentStepRef = useRef(currentStep);
  const lastSignatureRef = useRef(null);
  useEffect(() => { lastPushedAtRef.current = lastPushedAt; }, [lastPushedAt]);
  useEffect(() => { currentSongIdRef.current = currentSong?.songs_master_id || null; }, [currentSong?.songs_master_id]);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  // Persist ids for resume
  useEffect(() => {
    if (session?.session_id && participant?.participant_id) {
      localStorage.setItem('rapid_session_id', String(session.session_id));
      localStorage.setItem('rapid_participant_id', String(participant.participant_id));
    }
  }, [session?.session_id, participant?.participant_id]);

  // On mount: check resume
  useEffect(() => {
    const sessionId = session?.session_id || localStorage.getItem('rapid_session_id');
    const participantId = participant?.participant_id || localStorage.getItem('rapid_participant_id');
    if (!sessionId || !participantId) return;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    (async () => {
      try {
        const resp = await fetch(`${baseUrl}/api/live-quiz/sessions/${sessionId}/resume?participant_id=${participantId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('user_token') || ''}`
          }
        });
        if (!resp.ok) return;
        const data = await resp.json();
        setResumeInfo(data);
        setShowResumePrompt(true);
      } catch {}
    })();
  }, [session?.session_id, participant?.participant_id]);

  const handleResume = useCallback(() => {
    if (!resumeInfo) return;
    // Restore total score
    setTotalScore(resumeInfo.current_score || 0);
    // If there are active questions, resume into question view directly
    if (resumeInfo.has_active_questions && resumeInfo.questions?.length) {
      if (resumeInfo.pushed_at) setLastPushedAt(resumeInfo.pushed_at);
      setCurrentSong(resumeInfo.current_song);
      const formattedQuizData = {
        quiz_data: [{
          song: resumeInfo.current_song,
          questions: resumeInfo.questions
        }],
        difficulty: { base_time: 30 }
      };
      setQuizData(formattedQuizData);
      setQuestionVersion(prev => prev + 1);
      setCurrentStep(RAPID_QUIZ_STEPS.QUESTION);
    } else {
      setCurrentStep(RAPID_QUIZ_STEPS.WAITING);
    }
    setShowResumePrompt(false);
  }, [resumeInfo]);

  // Handle rules acknowledgment
  const handleRulesAcknowledged = useCallback(() => {
    setCurrentStep(RAPID_QUIZ_STEPS.WAITING);
  }, []);

  // Handle question received from waiting page
  const handleQuestionReceived = useCallback((questionData) => {
    // console.log('RapidQuizMain: Question received:', questionData);
    const { song, questions, pushed_at } = questionData;
    
    if (questions && questions.length > 0) {
      // Avoid processing same push repeatedly
      if (pushed_at && lastPushedAt && pushed_at === lastPushedAt) {
        return;
      }
      if (pushed_at) setLastPushedAt(pushed_at);
      setCurrentSong(song);
      
      // Format data for QuizQuestion component
      const formattedQuizData = {
        quiz_data: [{
          song: song,
          questions: questions
        }],
        difficulty: {
          base_time: 30 // Default time for rapid quiz questions
        }
      };
      // console.log('RapidQuizMain: Formatted quiz data:', formattedQuizData);
      setQuizData(formattedQuizData);
      // Force remount of question UI to avoid residual state
      setQuestionVersion(prev => prev + 1);
      setCurrentStep(RAPID_QUIZ_STEPS.QUESTION);
    }
  }, [lastPushedAt]);

  // Handle answer submission from QuizQuestion
  const submitAnswerToBackend = useCallback(async (answerData) => {
    if (!session?.session_id || !participant?.participant_id) return;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('user_token') || ''}`
      },
      body: JSON.stringify({
        participant_id: participant.participant_id,
        quizlet_results: answerData.results,
        total_score: answerData.results.totalScore
      })
    });
    if (!response.ok) throw new Error('Failed to submit answer');
    return response.json();
  }, [session?.session_id, participant?.participant_id]);

  const handleAnswerSubmitted = useCallback(async (quizletIndex, results) => {
    try {
      // Add to user answers
      const answerData = {
        quizletIndex: quizletIndex,
        results: results,
        timestamp: new Date().toISOString()
      };
      setUserAnswers(prev => [...prev, answerData]);
      
      // Update total score
      setTotalScore(prev => prev + results.totalScore);
      
      // Submit answer to backend
      await submitAnswerToBackend(answerData);
      
      // Move back to waiting for next push
      setTimeout(() => {
        setCurrentStep(RAPID_QUIZ_STEPS.WAITING);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting answer:', error);
      enqueueSnackbar('Failed to submit answer', { variant: 'error' });
    }
  }, [enqueueSnackbar, submitAnswerToBackend]);

  // Submit function is memoized above

  // Background watcher: detect new pushes even during play and switch immediately
  useEffect(() => {
    if (!session?.session_id) return;
    let socket;

    const applyNewQuestions = (data) => {
      // Build a stable signature to avoid redundant state updates
      const signature = `${data.current_song?.songs_master_id || 'na'}|${data.pushed_at || 'na'}|${data.total_questions || 0}`;
      if (lastSignatureRef.current === signature) return; // already applied
      lastSignatureRef.current = signature;

      if (data.pushed_at) setLastPushedAt(data.pushed_at);
      setCurrentSong(data.current_song);
      const formattedQuizData = {
        quiz_data: [{ song: data.current_song, questions: data.questions }],
        difficulty: { base_time: 30 }
      };
      setQuizData(prev => {
        // minimal shallow compare by signature to skip unnecessary setState
        return formattedQuizData;
      });
      setQuestionVersion(prev => prev + 1);
      setCurrentStep(RAPID_QUIZ_STEPS.QUESTION);
    };

    let unbindResync = () => {};
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      socket = io(baseUrl, { transports: ['websocket'] });
      const resyncQuestion = async () => {
        try {
          const res = await fetch(`${baseUrl}/api/live-quiz/rapid/sessions/${session.session_id}/current-questions`);
          if (!res.ok) return;
          const data = await res.json();
          if (data?.has_active_questions && Array.isArray(data.questions) && data.questions.length) {
            applyNewQuestions({
              session_id: session.session_id,
              current_song: data.current_song,
              questions: data.questions,
              pushed_at: data.pushed_at,
              total_questions: data.total_questions || data.questions.length,
            });
          }
        } catch (_) {}
      };
      socket.on('connect', () => {
        socket.emit('rapid:join', { sessionId: session.session_id });
        resyncQuestion();
      });
      unbindResync = bindGameResync(() => {
        if (socket.connected) socket.emit('rapid:join', { sessionId: session.session_id });
        resyncQuestion();
      });
      socket.on('rapid:question_pushed', (payload) => {
        // Only apply if it's a new push for this session
        if (!payload || payload.session_id !== session.session_id) return;
        if (!payload.pushed_at || payload.pushed_at === lastPushedAtRef.current) return;
        applyNewQuestions(payload);
      });
    } catch (_) {}

    return () => {
      unbindResync();
      try {
        if (socket) {
          socket.emit('rapid:leave', { sessionId: session.session_id });
          socket.disconnect();
        }
      } catch (_) {}
    };
  }, [session?.session_id]);

  // Handle session end
  const handleSessionEnd = useCallback(async () => {
    try {
      setSessionEnded(true);
      
      // Fetch final leaderboard
      const leaderboardResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/leaderboard`);
      if (leaderboardResponse.ok) {
        const leaderboardData = await leaderboardResponse.json();
        setFinalLeaderboard(leaderboardData.leaderboard || []);
        
        // Find user's final position
        const userIndex = leaderboardData.leaderboard.findIndex(entry => 
          entry.participant_id === participant?.participant_id
        );
        setFinalPosition(userIndex >= 0 ? userIndex + 1 : 0);
      }
      
      // Complete the session
      const completeResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('user_token') || ''}`
        },
        body: JSON.stringify({
          participant_id: participant.participant_id,
          final_score: totalScore,
          total_questions: userAnswers.length,
          correct_answers: userAnswers.filter(a => a.is_correct).length
        })
      });
      
      if (completeResponse.ok) {
        setCurrentStep(RAPID_QUIZ_STEPS.RESULTS);
      }
      
    } catch (error) {
      console.error('Error ending session:', error);
      enqueueSnackbar('Error completing session', { variant: 'error' });
    }
  }, [session, participant, totalScore, userAnswers, enqueueSnackbar]);

  // Check for session end
  useEffect(() => {
    if (!session?.session_id) return;
    
    const checkSessionStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/sessions/${session.session_id}`);
        if (response.ok) {
          const sessionData = await response.json();
          if (sessionData.status === 'ended' && !sessionEnded) {
            handleSessionEnd();
          }
        }
      } catch (error) {
        console.error('Error checking session status:', error);
      }
    };
    
    const interval = setInterval(checkSessionStatus, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [session?.session_id, sessionEnded, handleSessionEnd]);

  // Handle play again
  const handlePlayAgain = useCallback(() => {
    // Reset state
    setCurrentStep(RAPID_QUIZ_STEPS.RULES);
    setCurrentSong(null);
    setUserAnswers([]);
    setTotalScore(0);
    setFinalPosition(0);
    setFinalLeaderboard([]);
    setSessionEnded(false);
    setQuizData(null);
  }, []);

  // Handle go home
  const handleGoHome = useCallback(() => {
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case RAPID_QUIZ_STEPS.RULES:
        return (
          <>
            {showResumePrompt && resumeInfo && (
              <Box sx={{ maxWidth: 900, mx: 'auto', mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1">You have an ongoing session. Current score: {resumeInfo.current_score || 0}</Typography>
                  <Box>
                    <Button variant="contained" color="primary" onClick={handleResume} sx={{ mr: 1 }}>
                      Continue
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={handleRulesAcknowledged}>
                      Start New
                    </Button>
                  </Box>
                </Alert>
              </Box>
            )}
            <RapidQuizRules
              session={session}
              participant={participant}
              onBack={onBack}
              onAcknowledge={handleRulesAcknowledged}
            />
          </>
        );
        
      case RAPID_QUIZ_STEPS.WAITING:
        return (
          <RapidQuizWaiting
            session={session}
            participant={participant}
            onBack={onBack}
            onQuestionReceived={handleQuestionReceived}
            lastPushedAt={lastPushedAt}
          />
        );
        
      case RAPID_QUIZ_STEPS.QUESTION:
        return (
          <QuizQuestion
            key={questionVersion}
            config={{
              difficulty_id: session?.quiz_config?.difficulty_id || 7,
              duration_minutes: 5,
              questions_per_song: session?.quiz_config?.questions_per_song || 3
            }}
            quizData={quizData}
            currentQuizletIndex={0}
            onComplete={handleAnswerSubmitted}
            mode="rapid"
          />
        );
        
      case RAPID_QUIZ_STEPS.RESULTS:
        return (
          <RapidQuizResults
            session={session}
            participant={participant}
            userAnswers={userAnswers}
            finalScore={totalScore}
            finalPosition={finalPosition}
            leaderboard={finalLeaderboard}
            onBack={onBack}
            onPlayAgain={handlePlayAgain}
            onGoHome={handleGoHome}
          />
        );
        
      default:
        return null;
    }
  };

  return (
    <>
      {renderCurrentStep()}
    </>
  );
};

export default RapidQuizMain;
